import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Table, TextLink } from '@shared/components';
import { formatDateShort } from '@/app/utils/formatDate';
import { formatVerbatimMoneyCurrency } from '@shared/utils/formatMoneyDisplay';
import type { DealerFundStatementRow } from '../../types/dealerFundStatement.types';
import { shortId } from '../../utils/shortId';

interface Props {
    rows: DealerFundStatementRow[];
    currency: string;
    dealerNames: Map<string, string>;
    agreementRefs: Map<string, string>;
}

interface FundStatementTableMeta extends Record<string, unknown> {
    currency: string;
    dealerNames: Map<string, string>;
    agreementRefs: Map<string, string>;
}

const MONEY_CELL = 'block text-right tabular-nums';

// Module scope keeps cell identities stable; live values arrive through `table.options.meta`.
const columns: ColumnDef<DealerFundStatementRow>[] = [
    {
        id: 'agreement',
        header: 'Agreement',
        cell: ({ row, table }) => {
            const { agreementRefs } = table.options.meta as FundStatementTableMeta;
            const { agreementId } = row.original;
            const label = agreementRefs.get(agreementId.toLowerCase()) ?? null;
            return (
                <span title={agreementId}>
                    <TextLink
                        color="primary"
                        to={`/agreements/${agreementId}`}
                        ariaLabel={`Open agreement ${label ?? agreementId}`}
                    >
                        {label ?? shortId(agreementId)}
                    </TextLink>
                </span>
            );
        },
    },
    {
        id: 'sellingDealer',
        header: 'Selling dealer',
        cell: ({ row, table }) => {
            const { dealerNames } = table.options.meta as FundStatementTableMeta;
            const id = row.original.sellingDealerId;
            return <span title={id}>{dealerNames.get(id) ?? shortId(id)}</span>;
        },
    },
    { id: 'openedAt', header: 'Opened', cell: ({ row }) => formatDateShort(row.original.openedAt) },
    {
        id: 'balance',
        header: 'Fund balance',
        cell: ({ row, table }) => {
            const { currency } = table.options.meta as FundStatementTableMeta;
            return <span className={MONEY_CELL}>{formatVerbatimMoneyCurrency(row.original.balance, currency)}</span>;
        },
    },
    {
        id: 'remainingGross',
        header: 'Still to collect',
        cell: ({ row, table }) => {
            const { currency } = table.options.meta as FundStatementTableMeta;
            return <span className={MONEY_CELL}>{formatVerbatimMoneyCurrency(row.original.remainingGross, currency)}</span>;
        },
    },
];

const getRowId = (row: DealerFundStatementRow) => row.agreementId;

export function DealerFundStatementDataTable({ rows, currency, dealerNames, agreementRefs }: Props) {
    const meta = useMemo<FundStatementTableMeta>(() => ({ currency, dealerNames, agreementRefs }),
        [currency, dealerNames, agreementRefs],);

    return (
        <Table<DealerFundStatementRow>
            data={rows}
            columns={columns}
            meta={meta}
            enableSorting={false}
            emptyMessage="No agreements on this page."
            getRowId={getRowId}
        />
    );
}
